/**
 * Acessor de traduções a partir de catálogos estáticos — **sem React**.
 *
 * Mora aqui (e não em `@planuze/ui`) porque é lógica de i18n pura: assim código
 * server-side / não-UI (ex.: composer parsing, mapeamento de erro) resolve
 * `uiText`/`isTranslationKey` via `@planuze/translate/runtime` sem arrastar a
 * biblioteca de componentes React (ADR-0270).
 *
 * `currentLocale` é estado module-level mutável, atualizado pelo `I18nProvider`
 * (via `setUiTextLocale` no `onLocaleChange`). Componentes React re-renderizam
 * ao trocar locale (consumindo o context) e re-chamam `uiText` com a locale
 * atual. Fora de React, `uiText` usa a última locale conhecida.
 */
import { defaultLocale } from './catalogs.js';
import { createTranslator } from './lookup.js';
import {
  type Locale,
  type TranslationKey,
  type TranslationParams,
  translationKeys,
} from './types.js';

let currentLocale: Locale = defaultLocale;
let currentTranslator = createTranslator(defaultLocale);

/** Set O(1) das chaves do catálogo, para validar chaves vindas de fontes
 *  externas (ex.: `messageKey` de erro) antes de resolver via `uiText`. */
const translationKeySet: ReadonlySet<string> = new Set(translationKeys);

/** Narrowing seguro de string arbitrária para `TranslationKey`. Use antes de
 *  passar chave dinâmica a `uiText`; sem isso, `uiText` devolve a própria string
 *  crua quando a chave não existe no catálogo (vazamento de raw key). */
export function isTranslationKey(key: string): key is TranslationKey {
  return translationKeySet.has(key);
}

/** Chamado pelo `I18nProvider` quando o usuário troca o idioma. */
export function setUiTextLocale(next: Locale): void {
  if (next === currentLocale) return;
  currentLocale = next;
  currentTranslator = createTranslator(next);
}

export function uiText(key: TranslationKey, params?: TranslationParams): string {
  return currentTranslator(key, params);
}
